import { ref, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import { parseProviderBalance, type ProviderBalance } from './aiAgentContract'

/**
 * useProviderBalance — the Full-only DeepSeek provider-account balance composable.
 *
 * Reads the agent's balance passthrough via GET `/provider/balance` on the
 * ai-agent-service (`config.api.agentHost`). The route is Full-only, enforced
 * SERVER-SIDE; the frontend route guard is UX-only and not authoritative.
 *
 * The balance is the WHOLE provider account (`scope: 'provider_account'`),
 * shared across environments — never attributable to a single tenant/franchise.
 * The endpoint itself is fail-soft: a provider-side failure comes back as a
 * 200 with `{available:false, reason}`, never a thrown HTTP error. An HTTP
 * error here (401/403/network) is this composable's OWN transport failure,
 * surfaced via `error`/`ptError` exactly like `useAgentStats`.
 *
 * Network access lives ONLY here (never in the .vue), honoring the
 * `no_httpservice_in_vue` qa rule and CLAUDE.md rules 3/5.
 *
 * `skipErrorDisplay` so this composable owns the error surface (`error.value`);
 * PT messages mapped here, raw error text kept in the structured log only.
 */
export function useProviderBalance() {
  const { t } = useI18n()
  const balance: Ref<ProviderBalance | null> = ref(null)
  const loading: Ref<boolean> = ref(false)
  const error: Ref<string | null> = ref(null)

  function ptError(err: unknown): string {
    const status = (err as HttpError)?.status
    if (status === 401) {
      return t('Sua sessão expirou. Faça login novamente.')
    }
    if (status === 403) {
      return t('Você não tem permissão para ver o saldo do provedor.')
    }
    return t('Erro ao carregar o saldo do provedor. Tente novamente.')
  }

  async function load(): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const raw: unknown = await httpService.get(`${config.api.agentHost}/provider/balance`, {
        skipErrorDisplay: true,
        logContext: 'useProviderBalance',
      })
      balance.value = parseProviderBalance(raw)
      log('useProviderBalance', 'load', 'Saldo do provedor carregado', {
        available: balance.value.available,
      })
    } catch (err) {
      error.value = ptError(err)
      const message = err instanceof Error ? err.message : String(err)
      log('useProviderBalance', 'load', 'Falha ao carregar saldo do provedor', {
        message,
        status: (err as HttpError)?.status,
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    balance,
    loading,
    error,
    load,
  }
}
